import createCrudService from '@src/core/api/create-crud-service';
import { QUERY_KEYS } from '@src/shared/constants/query-keys';
import type { TUniversity } from '../types';
import type { TBaseQuery } from '@src/shared/types/base-query';
import type { TMember } from '@src/shared/types/member';
import type { TImage } from '@src/shared/types/image';
import { toast } from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';

import { request } from '@core/api/request';
import { queryClient } from '@core/api/query-client';
import { objectToFormData } from '@src/shared/utils/helpers/convert-obj-to-form-data';
import { showToast } from '@src/shared/utils/helpers/show-toast';

export type TUniversityQuery = {
  name?: string;
  status?: number;
} & TBaseQuery;

export type TUniversityPayload = {
  status?: number | string;
  logo?: number | TImage;
  country?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  name?: string;
  description?: string;
  main_contact_email?: string;
  main_contact_phone?: string;
  main_contact_name?: string;
  website?: string;
  manager_member?: number | TMember | null;
  import_history?: number;
  labels?: number[];
};

type TUniversityBulkAssignLabelsBody = {
  ids: number[];
  labels: number[];
};

const universitiesApi = {
  ...createCrudService<TUniversity, TUniversityQuery, TUniversityPayload>({
    queryKey: [QUERY_KEYS.getUniversities],
    url: 'universities',
  }),

  useReorderUniversityStatuses: () => {
    return useMutation({
      mutationFn: async (ids: number[]) => {
        return await request({
          url: 'university-statuses/reorder/',
          method: 'POST',
          body: { ids },
        });
      },
      onError: (error: string) => {
        toast.error(error);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.getUniversityStatuses],
        });
      },
    });
  },

  useImportUniversities: () => {
    return useMutation({
      mutationFn: async (file: File) => {
        return await request({
          url: 'universities/import-universities/',
          method: 'POST',
          body: objectToFormData({ file }),
        });
      },
      onError: (error: string) => {
        toast.error(error);
      },
      onSuccess: (res) => {
        res.detail &&
          showToast({
            title: 'Universities Import Started',
            description: res.detail,
            variant: 'success',
          });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.getUniversities],
        });
      },
    });
  },

  useBulkAssignUniversitiesLabels: () =>
    useMutation({
      mutationFn: async (body: TUniversityBulkAssignLabelsBody) =>
        await request<void>({
          url: 'universities/bulk-assign-labels/',
          method: 'PATCH',
          body,
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.getUniversities],
        });
        showToast({
          title: 'Bulk Assign Labels Success',
          description:
            'Labels have been assigned to the universities successfully',
          variant: 'success',
        });
      },
    }),
};

export default universitiesApi;
